/** Reading an audit row back as a sentence and a diff.
 *
 * The service records every admin decision as an action name, a target and
 * two JSON documents — what the row looked like before, and after. That is
 * the right thing to store and the wrong thing to show: a person scanning
 * the trail wants "held → pending on upload upl_8f2, by the owner", and only
 * opens the documents when a sentence is not enough.
 *
 * The sentences are written per action here, in the portal, because the
 * service has no business phrasing its own history and the names are stable
 * — dotted, past tense, and every one of them is in `src/routes/admin.ts`.
 * An action this build has never heard of still gets a sentence, made from
 * whichever keys changed, so the trail never shows a blank for a decision
 * the service was perfectly able to record.
 */

import { DASH } from '@/lib/format';
import type { AdminAuditEntry } from '@/lib/types';

export interface ChangeRow {
  key: string;
  before: string;
  after: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function same(left: unknown, right: unknown): boolean {
  return JSON.stringify(left ?? null) === JSON.stringify(right ?? null);
}

/** A value as a cell prints it: strings bare, nothing as a dash, the rest as
 *  compact JSON. */
export function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return DASH;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value) ?? DASH;
  } catch {
    return String(value);
  }
}

/** The keys whose value differs between the two documents, in the order
 *  they first appear. A document that is not an object is compared whole. */
export function changedKeys(before: unknown, after: unknown): ChangeRow[] {
  if (!isRecord(before) && !isRecord(after)) {
    if (same(before, after)) return [];
    return [{ key: 'value', before: formatValue(before), after: formatValue(after) }];
  }
  const left = isRecord(before) ? before : {};
  const right = isRecord(after) ? after : {};
  const keys = [...new Set([...Object.keys(left), ...Object.keys(right)])];
  return keys
    .filter((key) => !same(left[key], right[key]))
    .map((key) => ({ key, before: formatValue(left[key]), after: formatValue(right[key]) }));
}

/** Who did it, in words: a name where there is one, otherwise what kind of
 *  thing it was. */
export function actorLabel(
  entry: Pick<AdminAuditEntry, 'actor_id' | 'actor_kind' | 'actor_name'>,
): string {
  if (entry.actor_name) return entry.actor_name;
  if (entry.actor_kind === 'job') return 'the merge job';
  if (entry.actor_kind === 'system') return 'the service';
  return entry.actor_id ?? 'unknown';
}

/** `upload upl_8f2…`, or a dash for a decision about nothing in particular. */
export function targetLabel(entry: Pick<AdminAuditEntry, 'target_type' | 'target_id'>): string {
  const text = [entry.target_type, entry.target_id].filter(Boolean).join(' ');
  return text || DASH;
}

/** One line for the table: what the decision did, per action. */
export function auditSummary(
  entry: Pick<AdminAuditEntry, 'action' | 'target_type' | 'target_id' | 'before' | 'after'>,
): string {
  const before = isRecord(entry.before) ? entry.before : {};
  const after = isRecord(entry.after) ? entry.after : {};

  switch (entry.action) {
    case 'upload.status_changed':
      return `${formatValue(before['status'])} → ${formatValue(after['status'])}`;
    case 'user.disabled':
      return after['reason'] ? `disabled · ${formatValue(after['reason'])}` : 'disabled';
    case 'user.enabled':
      return 'enabled';
    case 'user.upload_interval_set': {
      const note = after['reason'] ? ` · ${formatValue(after['reason'])}` : '';
      return `upload interval ${formatValue(after['upload_interval_s'])} s (server: ${formatValue(after['server_interval_s'])} s)${note}`;
    }
    case 'user.upload_interval_reset':
      return `upload interval back to the server’s (was ${formatValue(before['upload_interval_s'])} s)`;
    case 'user.created':
      return `${formatValue(after['provider'])}:${formatValue(after['login'])} · ${formatValue(after['role'])}`;
    case 'identity.linked':
      return `linked ${formatValue(after['provider'])}:${formatValue(after['login'])}`;
    case 'issue.resolved': {
      const effects = after['effects'];
      const tail =
        Array.isArray(effects) && effects.length > 0
          ? ` · ${effects.map((effect) => formatValue(effect)).join('; ')}`
          : '';
      return `${formatValue(after['resolution'])}${tail}`;
    }
    case 'settings.updated':
      return changedKeys(before, after)
        .map((row) => `${row.key}: ${row.before} → ${row.after}`)
        .join(' · ');
    case 'service_key.rotated':
      return before['configured'] === true ? 'replaced the previous key' : 'issued the first key';
    case 'job.run_requested':
      return `${formatValue(after['scope'])} · ${formatValue(after['dispatch'])}`;
    case 'live.kicked':
      return formatValue(after['reason']);
    case 'drive_session.purged':
      return `${formatValue(before['track_name'] || before['official_id'])} · ${formatValue(before['laps'])} laps`;
    default: {
      const rows = changedKeys(entry.before, entry.after);
      const head = rows[0];
      if (!head) return DASH;
      const more = rows.length > 1 ? ` · +${rows.length - 1} more` : '';
      return `${head.key}: ${head.before} → ${head.after}${more}`;
    }
  }
}
